import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotificationService } from '@app/core/notifications/notification.service';
import { CommandFactoryService } from '@app/features/editeur/services/command-factory.service';
import { EspaceService } from '@app/features/editeur/services/espace.service';
import { TreeViewService } from '@app/features/editeur/services/tree-view.service';
import { ObjetMaquetteSummary } from '@pcscol/odf-api-v1-angular';
import { Observable, combineLatest } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { TreeNode } from '../../treeview/tree-node';
import { CreerObjetMaquetteComponent } from '../creer-objet-maquette/creer-objet-maquette.component';
import { BibliothequeComponent } from '../bibliotheque/bibliotheque.component';
import { EditeurObjetMaquetteComponent } from '../editeur-objet-maquette/editeur-objet-maquette.component';
import { AsyncPipe } from '@angular/common';

enum SidebarView {
    BIBLIOTHEQUE,
    EDITER_OBJET_MAQUETTE,
    CREER_OBJET_MAQUETTE
}

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    imports: [CreerObjetMaquetteComponent, BibliothequeComponent, EditeurObjetMaquetteComponent, AsyncPipe]
})
export class SidebarComponent {
    private _treeview = inject(TreeViewService);
    private _espaceService = inject(EspaceService);
    private _notificationService = inject(NotificationService);
    private _commandFactory = inject(CommandFactoryService);
    private _route = inject(ActivatedRoute);

    SidebarView = SidebarView;
    readonly espace$ = this._espaceService.espaceSelected$;
    readonly racine$ = this._treeview.racineMaquette$;
    readonly objetMaquetteSelected$ = this._treeview.objetMaquetteSelected$;
    readonly parentSelectedForAddingChild$ = this._treeview.parentSelectedForAddingChild$;
    readonly contexteIdSelected$ = this._treeview.contexteIdSelected$;

    espaceIdInUrl$: Observable<string | null> = this._route.paramMap.pipe(
        map(
            p => p.get('espaceId'),
            tap((espaceId: string) => {
                this._espaceService.selectEspaceById(espaceId);
            })
        )
    );

    public get view$(): Observable<SidebarView> {
        return combineLatest([this.racine$, this.objetMaquetteSelected$, this.parentSelectedForAddingChild$]).pipe(
            map(([racine, objetMaquetteSelected, parentSelectedForAddingChild]) => {
                if (parentSelectedForAddingChild !== null && !parentSelectedForAddingChild.enfantDejaExistant) {
                    return SidebarView.CREER_OBJET_MAQUETTE;
                }
                if (parentSelectedForAddingChild !== null && parentSelectedForAddingChild.enfantDejaExistant) {
                    return SidebarView.BIBLIOTHEQUE;
                }
                if (racine?.id == null) {
                    return SidebarView.BIBLIOTHEQUE;
                }
                if (objetMaquetteSelected?.id != null) {
                    return SidebarView.EDITER_OBJET_MAQUETTE;
                }
                return SidebarView.BIBLIOTHEQUE;
            })
        );
    }

    enfantAjouteCallback(parentId: string): void {
        this._notificationService.success(`L'enfant a été ajouté avec succès`);
        this._treeview.clearParentSelectedForAddingChild();
        this._treeview.expandNode(parentId);
    }

    annulerAjoutEnfant(): void {
        this._treeview.clearParentSelectedForAddingChild();
    }

    closeSelectedObjetMaquette(): void {
        this._treeview.selectObjetMaquette(null);
    }

    doAjouterEnfant(event: { parent: TreeNode; enfant: ObjetMaquetteSummary }): void {
        this._commandFactory
            .ajouterEnfantCommand(event.parent.id, '0', { id: event.enfant.id, obligatoire: true })
            .do()
            .pipe(first())
            .subscribe(() => {
                this._treeview.clearParentSelectedForAddingChild();
                this._treeview.selectObjetMaquette(null);
            });
    }
}
